import { create } from 'zustand'

type FilterMode = 'all' | 'active'
type ViewMode = 'daily' | 'monthly'

interface UIState {
  sidebarOpen: boolean
  viewMode: ViewMode
  filterMode: FilterMode
  dateRange: { start: string; end: string }
  collapsedCategories: Set<string>
  unitMode: 'yen' | 'thousand'
  /** 保存中のmutation数（>0なら未完了操作あり） */
  pendingMutations: number
  /** シークレットモード: ON で機密ページ（資金繰り表/ダッシュボード等）が表示される。
      admin はクライアント側で常に true 扱い、それ以外は permission ある人がトグル切替。
      localStorage で永続化（ブラウザ閉じても維持）。 */
  secretMode: boolean

  toggleSidebar: () => void
  setViewMode: (mode: ViewMode) => void
  setFilterMode: (mode: FilterMode) => void
  setDateRange: (start: string, end: string) => void
  toggleCategory: (categoryId: string) => void
  setUnitMode: (mode: 'yen' | 'thousand') => void
  incrementPending: () => void
  decrementPending: () => void
  setSecretMode: (mode: boolean) => void
}

// 今月1日 ~ 3ヶ月後の末日をデフォルト期間にする
function getDefaultDateRange() {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), 1)
  const end = new Date(now.getFullYear(), now.getMonth() + 3, 0)
  return {
    start: formatDate(start),
    end: formatDate(end),
  }
}

function formatDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// モバイル（lg未満 = <1024px）判定: 資金繰り表は横に広いので初期は月次モードが見やすい
const isMobileViewport = typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(max-width: 1023px)').matches

const SECRET_STORAGE_KEY = 'cf_secret_mode'
function getInitialSecretMode(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(SECRET_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export const useUIStore = create<UIState>((set) => ({
  sidebarOpen: !isMobileViewport,
  viewMode: isMobileViewport ? 'monthly' : 'daily',
  filterMode: 'active',
  dateRange: getDefaultDateRange(),
  collapsedCategories: new Set<string>(),
  unitMode: 'yen',
  pendingMutations: 0,
  secretMode: getInitialSecretMode(),

  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setViewMode: (mode) => set({ viewMode: mode }),
  setFilterMode: (mode) => set({ filterMode: mode }),
  setDateRange: (start, end) => set({ dateRange: { start, end } }),
  toggleCategory: (id) =>
    set((s) => {
      const next = new Set(s.collapsedCategories)
      next.has(id) ? next.delete(id) : next.add(id)
      return { collapsedCategories: next }
    }),
  setUnitMode: (mode) => set({ unitMode: mode }),
  incrementPending: () => set((s) => ({ pendingMutations: s.pendingMutations + 1 })),
  decrementPending: () => set((s) => ({ pendingMutations: Math.max(0, s.pendingMutations - 1) })),
  setSecretMode: (mode) => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(SECRET_STORAGE_KEY, mode ? '1' : '0')
      } catch { /* ignore */ }
    }
    set({ secretMode: mode })
  },
}))
